import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface SessionCreationResponse {
  sessionId: string;
  sessionName: string;
  passcode: string;
  invitationLink: string;
  whipEndpoint: string;
  createdAt: string;
  maxListeners: number;
}

export default function CreateSession() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const [sessionName, setSessionName] = useState('');
  const [maxListeners, setMaxListeners] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState<SessionCreationResponse | null>(null);
  const [copiedField, setCopiedField] = useState<'passcode' | 'link' | null>(null);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const token = apiClient.getAuthToken();
      if (!token) {
        setError('You must be logged in to create a session');
        setIsLoading(false);
        return;
      }

      const response = await apiClient.authenticatedRequest<SessionCreationResponse>(
        '/api/sessions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionName: sessionName.trim(),
            maxListeners,
          }),
        }
      );

      setSessionData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async (text: string, field: 'passcode' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleStartStreaming = () => {
    if (sessionData) {
      navigate(`/host/${sessionData.sessionId}`, {
        state: {
          passcode: sessionData.passcode,
          invitationLink: sessionData.invitationLink,
          sessionName: sessionData.sessionName,
        },
      });
    }
  };

  if (sessionData) {
    return (
      <div className="create-session-container">
        <h1>Session Created!</h1>
        
        <div className="session-info">
          <h2>{sessionData.sessionName}</h2>
          
          <div className="info-section">
            <h3>Session Passcode</h3>
            <div className="copy-field">
              <code className="passcode">{sessionData.passcode}</code>
              <button
                type="button"
                onClick={() => handleCopyToClipboard(sessionData.passcode, 'passcode')}
                className="copy-button"
                aria-label="Copy passcode to clipboard"
              >
                {copiedField === 'passcode' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="help-text">Share this 6-character code with listeners</p>
          </div>

          <div className="info-section">
            <h3>Invitation Link</h3>
            <div className="copy-field">
              <input
                type="text"
                value={sessionData.invitationLink}
                readOnly
                className="invitation-link"
                aria-label="Invitation link"
              />
              <button
                type="button"
                onClick={() => handleCopyToClipboard(sessionData.invitationLink, 'link')}
                className="copy-button"
                aria-label="Copy invitation link to clipboard"
              >
                {copiedField === 'link' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="help-text">Or share this direct link for instant access</p>
          </div>

          <div className="info-section">
            <p><strong>Max Listeners:</strong> {sessionData.maxListeners}</p>
            <p><strong>Created:</strong> {new Date(sessionData.createdAt).toLocaleString()}</p>
          </div>

          <div className="button-group">
            <button
              type="button"
              onClick={handleStartStreaming}
              className="primary-button"
            >
              Start Streaming
            </button>
            <button
              type="button"
              onClick={() => {
                setSessionData(null);
                setSessionName('');
                setMaxListeners(100);
              }}
              className="secondary-button"
            >
              Create Another Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-session-container">
      <div className="header-with-logout">
        <h1>Create Streaming Session</h1>
        <div className="user-info">
          <span>Welcome, {user?.displayName}</span>
          <button
            type="button"
            onClick={handleLogout}
            className="logout-button"
          >
            Logout
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="sessionName">Session Name</label>
          <input
            id="sessionName"
            type="text"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            required
            minLength={1}
            maxLength={100}
            placeholder="My Silent Disco"
            disabled={isLoading}
            aria-required="true"
          />
          <p className="help-text">Give your session a memorable name (1-100 characters)</p>
        </div>

        <div className="form-group">
          <label htmlFor="maxListeners">Maximum Listeners</label>
          <input
            id="maxListeners"
            type="number"
            value={maxListeners}
            onChange={(e) => setMaxListeners(parseInt(e.target.value, 10))}
            required
            min={1}
            max={1000}
            disabled={isLoading}
            aria-required="true"
          />
          <p className="help-text">Set the maximum number of concurrent listeners (1-1000)</p>
        </div>
        
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Session'}
        </button>
      </form>
    </div>
  );
}
